import DialogContent from "@mui/material/DialogContent/DialogContent";
import { DialogActions } from "@mui/material";
import Dialog from "@mui/material/Dialog/Dialog";
import { Form } from "react-final-form";
import { HighlightOff } from "@mui/icons-material";

import { useTranslation } from "@/lib/i18n";
import { TextField } from "@/features/shared/components";
import Regex from "@/features/shared/regex";
import { useAddUserMutation } from "@/features/admin/features/users/hooks/queries";

import { DialogTitle, Button } from "./styled";

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  organisationId: string;
}

interface FormValues {
  email: string;
}

const AddUserDialog = ({ open, onClose, organisationId }: AddUserDialogProps) => {
  const { t } = useTranslation();
  const addUserMutation = useAddUserMutation(organisationId);

  const onSubmit = async ({ email }: FormValues) => {
    addUserMutation.mutate(
      {
        email,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const validate = ({ email }: FormValues) => {
    const errors: { [K in keyof FormValues]?: string } = {};
    if (!Regex.EMAIL.test(email)) {
      errors.email = t("form_validation.invalid", {
        field: t("admin.users.add_user.fields.email.label"),
      });
    }

    return errors;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Form<FormValues>
        onSubmit={onSubmit}
        validate={validate}
        validateOnBlur={true}
        render={({ handleSubmit, errors, touched }) => (
          <>
            <DialogTitle>
              {t("admin.users.add_user.title")} <HighlightOff onClick={onClose} />
            </DialogTitle>
            <DialogContent>
              <form onSubmit={handleSubmit}>
                <TextField
                  sx={{ mt: 1 }}
                  autoFocus
                  error={!!errors?.email && touched?.email}
                  variant="outlined"
                  name="email"
                  label={t("admin.users.add_user.fields.email.label")}
                  placeholder={t("admin.users.add_user.fields.email.placeholder")}
                />

                {addUserMutation.isPending && <p>{t("admin.users.add_user.loading")}</p>}
              </form>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center" }}>
              <Button
                disabled={addUserMutation.isPending}
                onClick={handleSubmit}
                variant="rounded"
                size="extraLarge"
              >
                {t("admin.users.add_user.invite")}
              </Button>
            </DialogActions>
          </>
        )}
      />
    </Dialog>
  );
};

export default AddUserDialog;
